import React, { ReactNode, createContext, useEffect, useState } from 'react';
import { changeCompany, getAvailableCompanies } from './services/services';
import { refreshAccessTokenSetLocalStorage } from 'services/api';
import { decodeToken } from 'utils/checkFunctionalityPermission';
import { useSessionStorage } from 'hooks/useSessionStorage';
import { loggerInfo } from 'utils/infoLog';
import { useAuth } from 'hooks/useAuth';
import Analytics from 'helpers/analytics';
import { getMeInfo } from 'services/services';

type companyProps = {
	image: string;
	businessName: string;
	fancyName: string;
	id: string;
};

export const BrokersContext = createContext({
	availableCompanies: [],
	isBroker: false,
	selectedCompany: {
		image: '',
		businessName: '',
		fancyName: '',
		id: '',
	},
	onSelectedCompany: (e: any) => e,
	isLoadingAvailableCompanies: false,
	isLoadingChangeCompany: false,
});

type BrokersProviderProps = {
	children: ReactNode;
};

export const BrokersProvider = ({
	children,
}: BrokersProviderProps): JSX.Element => {
	const { isAuthenticated } = useAuth();
	const token: any = decodeToken();

	const isBroker = token.broker === 'true' ? true : false;
	const [availableCompanies, setAvailableCompanies] = useState<any>([]);
	const [selectedCompany, setSelectedCompany] = useState<any>({});
	const [isLoadingAvailableCompanies, setIsLoadingAvailableCompanies] =
		useState(false);
	const [isLoadingChangeCompany, setIsLoadingChangeCompany] = useState(false);
	const [success, setSuccess] = useState(false);

	const { setSessionStorage } = useSessionStorage();

	const onChangeLocalStorageToken = (token: string) =>
		setSessionStorage('idToken', JSON.stringify(token));

	const onSelectedCompany: any = async (company: any) => {
		setIsLoadingChangeCompany(true);
		try {
			await changeCompany(company.id);
			setSelectedCompany(company);
			await refreshAccessTokenSetLocalStorage({ onChangeLocalStorageToken });
			const { data } = await getMeInfo();
			setSessionStorage('me', JSON.stringify(data.content));
			Analytics.track('broker', 'Change Company', {
				isBroker,
				company: company,
			});
			setSuccess(true);
		} catch (error) {
			console.error(error);
			setSuccess(false);
		} finally {
			setIsLoadingChangeCompany(false);
		}
	};

	useEffect(() => {
		if (success) window.location.reload();
	}, [success]);

	const fetchCompanies = async () => {
		setIsLoadingAvailableCompanies(true);
		const token: any = decodeToken();
		const companyIdToken = token['custom:companyId'];

		try {
			const {
				data: { content },
			} = await getAvailableCompanies();
			setAvailableCompanies(content);
			const initialSelectedCompanyByToken = content?.find(
				(company: companyProps) => company.id === companyIdToken,
			);
			if (initialSelectedCompanyByToken) {
				setSelectedCompany(initialSelectedCompanyByToken);
			} else {
				loggerInfo({
					data: 'Error trying to find the user company',
					context: 'access',
					type: 'info',
					location: 'BrokersProvider',
				});
			}
		} catch (error) {
			loggerInfo({
				data: error,
				context: 'access',
				location: 'BrokersProvider',
			});
		} finally {
			setIsLoadingAvailableCompanies(false);
		}
	};

	useEffect(() => {
		if (isBroker && isAuthenticated) {
			fetchCompanies();
		}
	}, [isAuthenticated, isBroker]);

	return (
		<>
			<BrokersContext.Provider
				value={{
					availableCompanies,
					isBroker,
					selectedCompany,
					onSelectedCompany,
					isLoadingAvailableCompanies,
					isLoadingChangeCompany,
				}}
			>
				{children}
			</BrokersContext.Provider>
		</>
	);
};
